import React, { useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@column/ui';

import { DocumentExtensionType, DocumentRepository, DocumentTypeMap, Report } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { downloadFile } from '~/util';
import { getFilenameForStatementReport } from '~/util/reporting';

interface ReportFile {
  ext: DocumentExtensionType;
  documentId: string;
}

export const getReportFiles = (report: Report): ReportFile[] =>
  [...DocumentTypeMap].flatMap(([ext, { fieldId }]) => {
    const documentId = report[fieldId as keyof Report] as string | undefined;
    return documentId ? [{ ext, documentId }] : [];
  });

interface ReportDownloadMenuProps {
  report: Report;
  size?: 'xs' | 'sm';
  variant?: 'primary' | 'secondary';
}

export const ReportDownloadMenu: React.FC<ReportDownloadMenuProps> = ({ report, size = 'sm', variant = 'primary' }) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const [running, setRunning] = useState<boolean>(false);
  const files = getReportFiles(report);

  const handleDownload = ({ ext, documentId }: ReportFile) => {
    const fileName = getFilenameForStatementReport(report, ext);

    setRunning(true);

    DocumentRepository.get(documentId, { contentDisposition: `attachment; filename="${fileName}"` })
      .then((response) => downloadFile(response.url, fileName))
      .catch((error) => addDangerNotification({ content: error.message }));
  };

  const iconClassName =
    size === 'xs' ? '[--icon-size-x:14px] [--icon-size-y:14px]' : '[--icon-size-x:16px] [--icon-size-y:16px]';
  const [singleFile] = files.length === 1 ? files : [];

  const button = (
    <Button
      type="button"
      size={size}
      variant={variant}
      disabled={files.length === 0}
      aria-label={singleFile ? `Download .${singleFile.ext}` : undefined}
      onClick={singleFile ? () => handleDownload(singleFile) : undefined}
    >
      <Icon.AnimationDownload running={running} onDone={() => setRunning(false)} className={iconClassName} />
      Download
    </Button>
  );

  if (files.length <= 1) {
    return button;
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="min-w-36">
        {files.map((file) => (
          <DropdownMenuItem key={file.ext} onSelect={() => handleDownload(file)}>
            .{file.ext}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
